'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  faqs: FAQ[];
}

export default function FAQSection({ faqs }: FAQSectionProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const toggleFAQ = (index: number) => {
    if (!isHydrated) return;
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  return (
    <section className="py-16 bg-card">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-muted-foreground font-body">
            Get answers to common questions about the program.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-surface rounded-xl border border-border overflow-hidden hover:shadow-sky-sm transition-all duration-300"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between hover:bg-card/50 transition-colors duration-300"
              >
                <span className="text-left text-base font-headline font-semibold text-foreground pr-4">
                  {faq.question}
                </span>
                <Icon
                  name="ChevronDownIcon"
                  size={20}
                  variant="outline"
                  className={`text-muted-foreground flex-shrink-0 transition-transform duration-300 ${
                    expandedFAQ === index ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {expandedFAQ === index && (
                <div className="px-6 pb-5 border-t border-border">
                  <p className="pt-4 text-sm text-muted-foreground font-body leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground font-body mb-4">
            Still have questions? We're here to help!
          </p>
          <button className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-headline font-semibold hover:bg-accent transition-colors duration-300">
            Contact Support
          </button>
        </div>
      </div>
    </section>
  );
}